import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Award, Bell, ChevronDown, Gift, LogOut, Menu, User as UserIcon } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../auth/auth.store';

/** Formata cents em BRL curto (ex.: R$90). */
function fmtCreditBrl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });
}

/** Tempo relativo curto pt-BR (ex.: "há 5 min", "há 2 h", "há 3 d"). */
function shortAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  return `há ${d} d`;
}

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard B3',
  '/accounts': 'Minhas contas',
  '/withdrawals': 'Saques',
  '/replicator': 'Replicador',
  '/documents': 'Documentos',
  '/notifications': 'Notificações',
  '/support': 'Suporte',
};

export function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const notifQuery = useQuery({
    queryKey: ['trader-notifications'],
    queryFn: () => api.getTraderNotifications(),
    refetchInterval: 60_000,
  });
  const unread = notifQuery.data?.unreadCount ?? 0;
  const notifications = notifQuery.data?.items ?? [];

  // Botão "Excelência Trading" — configurável pelo admin (liga + URL externa).
  const meQuery = useQuery({ queryKey: ['trader-me'], queryFn: () => api.getTraderMe() });
  const excellence = meQuery.data?.excellence;
  const creditCents = meQuery.data?.trader?.creditBalanceCents ?? 0;

  function openNotification(): void {
    setNotifOpen(false);
    navigate('/notifications');
  }

  const initial =
    user?.fullName?.trim().charAt(0).toUpperCase() ?? user?.email?.charAt(0).toUpperCase() ?? 'T';
  const title = PAGE_TITLES[location.pathname] ?? 'Trader';

  return (
    <header className="force-dark bg-brand-900 sticky top-0 z-10 flex h-14 items-center justify-between border-b border-white/5 px-6 lg:px-8">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onMenuClick}
          className="text-ink-200 -ml-1 rounded-md p-2 transition-colors hover:bg-white/5 hover:text-white lg:hidden"
          aria-label="Abrir menu"
        >
          <Menu size={18} />
        </button>
        <p className="text-ink-200 text-sm font-semibold">{title}</p>
      </div>

      <div className="flex items-center gap-2">
        {excellence?.enabled && excellence.url && (
          <a
            href={excellence.url}
            target="_blank"
            rel="noreferrer"
            className="bg-brand-500 hover:bg-brand-600 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors"
          >
            <Award size={14} /> Excelência Trading
          </a>
        )}
        {creditCents > 0 && (
          <Link
            to="/creditos"
            title="Seu crédito de indicação"
            className="text-ink-100 inline-flex items-center gap-1.5 rounded-md bg-white/5 px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-white/10"
          >
            <Gift size={14} className="text-brand-300" /> {fmtCreditBrl(creditCents)}
          </Link>
        )}
        <div className="relative">
          <button
            type="button"
            onClick={() => setNotifOpen((v) => !v)}
            className="text-ink-200 relative rounded-md p-2 transition-colors hover:bg-white/5 hover:text-white"
            title={unread > 0 ? `${unread} não lida(s)` : 'Notificações'}
            aria-label="Notificações"
          >
            <Bell size={16} />
            {unread > 0 && (
              <span className="bg-danger-500 text-2xs absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-full px-1 font-bold text-white">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>

          {notifOpen && (
            <>
              {/* backdrop pra fechar ao clicar fora */}
              <div
                className="fixed inset-0 z-20"
                onClick={() => setNotifOpen(false)}
                role="presentation"
              />
              <div className="border-ink-800 bg-ink-900 absolute right-0 z-30 mt-2 w-80 overflow-hidden rounded-lg border shadow-lg">
                <div className="border-ink-800 flex items-center justify-between border-b px-4 py-2.5">
                  <p className="text-ink-100 text-sm font-semibold">Notificações</p>
                  {unread > 0 && (
                    <span className="text-2xs text-brand-300">{unread} não lida(s)</span>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <p className="text-ink-400 px-4 py-6 text-center text-sm">Nenhuma notificação.</p>
                ) : (
                  <ul className="divide-ink-800 max-h-80 divide-y overflow-y-auto">
                    {notifications.slice(0, 8).map((n) => {
                      const isUnread = !n.readAt;
                      return (
                        <li key={n.id}>
                          <button
                            type="button"
                            onClick={openNotification}
                            className="hover:bg-ink-800/70 flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors"
                          >
                            <span
                              className={
                                'mt-1.5 h-2 w-2 shrink-0 rounded-full ' +
                                (isUnread ? 'bg-brand-400' : 'bg-transparent')
                              }
                            />
                            <span className="min-w-0 flex-1">
                              <span className="text-ink-100 block truncate text-sm font-medium">
                                {n.title}
                              </span>
                              {n.body && (
                                <span className="text-ink-400 mt-0.5 block truncate text-xs">
                                  {n.body}
                                </span>
                              )}
                              <span className="text-ink-500 text-2xs mt-0.5 block">
                                {shortAgo(n.createdAt)}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <Link
                  to="/notifications"
                  onClick={() => setNotifOpen(false)}
                  className="border-ink-800 text-brand-300 hover:bg-ink-800/60 block border-t px-4 py-2.5 text-center text-xs font-semibold"
                >
                  Ver todas
                </Link>
              </div>
            </>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5"
          >
            <div className="text-ink-200 grid h-7 w-7 place-items-center rounded-full bg-white/10 text-xs font-semibold">
              {initial}
            </div>
            <span className="text-ink-200 text-sm font-medium">
              {user?.fullName ?? user?.email ?? 'Trader'}
            </span>
            <ChevronDown size={14} className="text-ink-400" />
          </button>

          {open && (
            <div className="border-ink-800 bg-ink-900 absolute right-0 mt-2 w-56 rounded-lg border shadow-lg">
              <div className="border-ink-800 border-b px-4 py-3">
                <p className="text-ink-100 truncate text-sm font-semibold">
                  {user?.fullName ?? 'Trader'}
                </p>
                <p className="text-ink-400 truncate text-xs">{user?.email}</p>
              </div>
              <ul className="py-1">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate('/perfil');
                    }}
                    className="text-ink-300 hover:bg-ink-800 flex w-full items-center gap-2 px-4 py-2 text-sm"
                  >
                    <UserIcon size={14} /> Meu perfil
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      void logout();
                    }}
                    className="text-danger-300 hover:bg-ink-800 flex w-full items-center gap-2 px-4 py-2 text-sm"
                  >
                    <LogOut size={14} /> Sair
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
